/**
 * Drive simulation: runs the real pipeline (TrackFilter -> ProximityEngine -> SpeechQueue) over a
 * track on virtual time and records where every narration was triggered, started and finished.
 * A three-hour drive takes well under a second. Shared by the replay assertions, the review page
 * and the browser demo, so the three can never disagree about what the phone would have said.
 */
import { featureItem, FeatureScheduler, type FeatureSchedulerOptions } from './features';
import { areasNear, FillerScheduler, type FillerOptions } from './filler';
import { haversineM } from './geo';
import type { TrackPoint } from './gpx';
import { narrationIntro, spokenTitle } from './intro';
import { isPlace, ProximityEngine } from './proximity';
import { replayFixes } from './replay';
import { SpeechQueue, type DropReason, type FinishReason, type SpeechItem } from './speechQueue';
import { TrackFilter } from './track';
import type { Area, AreaSighting, CleanFix, Feature, FeatureFixture, Landmark, SpeakOptions, Speaker } from './types';
import { VirtualClock } from './virtualClock';

export interface SimOptions {
  /** GPS jitter amplitude (metres). 0 = clean. */
  noiseM?: number;
  /** [startSec, endSec] windows with no fixes (a tunnel). */
  dropoutRanges?: [number, number][];
  seed?: number;
  /** Speaking pace used to time the stub voice. */
  wordsPerSecond?: number;
  /** Virtual time allowed after the last fix so the final narration can finish. */
  settleMs?: number;
  /** Area rundowns and where the corridor calls reported them; without these no filler plays. */
  areas?: { areas: Area[]; sightings: AreaSighting[] };
  filler?: Partial<FillerOptions>;
  /** Rivers, forests and wilderness; without these none plays. */
  features?: FeatureFixture;
  along?: Partial<FeatureSchedulerOptions>;
}

/** A position along the drive, `t` seconds after the first fix. */
export interface SimSample {
  t: number;
  lat: number;
  lon: number;
  /** Track-derived heading, as the phone's arrow sees it; null until the car has covered 100 m. */
  heading?: number | null;
}

export interface SimNarration {
  pageId: number;
  landmark: Landmark;
  /** The opener actually spoken ("Coming up on your left in about a minute: X."). */
  intro: string;
  triggeredAt: number;
  triggerPos: SimSample;
  startedAt: number;
  startPos: SimSample | null;
  endedAt: number | null;
  endPos: SimSample | null;
  reason: FinishReason | null;
  distAtTrigger: number;
  distAtStart: number;
  distAtEnd: number | null;
  /** Speed-extended trigger distance that fired (metres). */
  triggerRadius: number;
  speedMps: number | null;
}

/** An area rundown played as filler. */
export interface SimFiller {
  area: Area;
  /** Silence before it started (seconds). */
  quietSec: number;
  startedAt: number;
  startPos: SimSample | null;
  endedAt: number | null;
  endPos: SimSample | null;
  reason: FinishReason | null;
}

/** A river, forest or wilderness told because the road ran beside or inside it. */
export interface SimFeature {
  feature: Feature;
  /** Metres run beside it when it was queued. */
  runM: number;
  queuedAt: number;
  startedAt: number;
  startPos: SimSample | null;
  endedAt: number | null;
  endPos: SimSample | null;
  reason: FinishReason | null;
  /** True when the fixture had no script and a placeholder of about a minute stood in. */
  placeholder: boolean;
}

/** A landmark kept quiet because a neighbour's story, already spoken, covered it. */
export interface SimCovered {
  landmark: Landmark;
  by: number;
  at: number;
}

export interface SimDropped {
  landmark: Landmark;
  at: number;
  reason: DropReason;
  dist: number;
}

export interface SimClosest {
  d: number;
  t: number;
  lat: number;
  lon: number;
}

export interface SimResult {
  narrations: SimNarration[];
  fillers: SimFiller[];
  features: SimFeature[];
  dropped: SimDropped[];
  covered: SimCovered[];
  /** Every accepted fix, in order. */
  path: SimSample[];
  /** Closest approach to each landmark along the raw track, by page_id. */
  closest: Map<number, SimClosest>;
  rejects: TrackFilter['rejects'];
  fixesIn: number;
  fixesAccepted: number;
  maxQueue: number;
  durationSec: number;
}

/** Speaks on virtual time: onDone fires after the text's spoken length unless stopped. */
export class TimedStubSpeaker implements Speaker {
  speaking = false;
  private timer: unknown = null;
  private current: SpeakOptions | null = null;
  constructor(
    private clock: VirtualClock,
    private wordsPerSecond = 2.6,
  ) {}
  speak(text: string, opts: SpeakOptions): void {
    this.speaking = true;
    this.current = opts;
    const ms = (text.trim().split(/\s+/).length / this.wordsPerSecond) * 1000;
    this.timer = this.clock.setTimeout(() => {
      this.speaking = false;
      this.current = null;
      opts.onDone();
    }, ms);
  }
  stop(): void {
    if (!this.speaking) return;
    this.clock.clearTimeout(this.timer);
    this.speaking = false;
    const c = this.current;
    this.current = null;
    c?.onStopped();
  }
  isSpeaking(): boolean {
    return this.speaking;
  }
}

export async function simulateDrive(track: TrackPoint[], landmarks: Landmark[], opts: SimOptions = {}): Promise<SimResult> {
  const t0 = 1_700_000_000_000;
  const clock = new VirtualClock(t0);
  const speaker = new TimedStubSpeaker(clock, opts.wordsPerSecond ?? 2.6);
  const played = new Set<number>();
  const narrations: SimNarration[] = [];
  const fillers: SimFiller[] = [];
  const featureLog: SimFeature[] = [];
  const featureById = new Map((opts.features?.features ?? []).map((f) => [f.page_id, withScript(f)]));
  const areaById = new Map((opts.areas?.areas ?? []).map((a) => [a.page_id, a]));
  const dropped: SimDropped[] = [];
  const covered: SimCovered[] = [];
  const pending = new Map<number, SimNarration>();
  const path: SimSample[] = [];
  const byId = new Map(landmarks.map((l) => [l.page_id, l]));
  let cur: CleanFix | null = null;
  let maxQueue = 0;

  const sec = (): number => (clock.now() - t0) / 1000;
  const here = (): SimSample | null => (cur ? { t: sec(), lat: cur.coords.latitude, lon: cur.coords.longitude, heading: cur.heading } : null);

  const queue = new SpeechQueue(speaker, {
    clock,
    onStart: (item) => {
      played.add(item.pageId);
      if (item.kind === 'feature') {
        const f = featureLog.find((x) => x.feature.page_id === item.pageId && Number.isNaN(x.startedAt));
        if (f) {
          f.startedAt = sec();
          f.startPos = here();
          // A branch told as one covers the river it belongs to.
          if (f.feature.parent_page_id != null) played.add(f.feature.parent_page_id);
        }
        return;
      }
      if (item.kind === 'filler') {
        const f = fillers[fillers.length - 1];
        if (f && f.area.page_id === item.pageId) {
          f.startedAt = sec();
          f.startPos = here();
        }
        return;
      }
      const n = pending.get(item.pageId);
      if (!n) return;
      const p = here();
      n.startedAt = sec();
      n.startPos = p;
      n.distAtStart = p ? haversineM(p.lat, p.lon, n.landmark.lat, n.landmark.lon) : n.distAtTrigger;
      narrations.push(n);
    },
    onFinish: (item, reason) => {
      if (item.kind === 'feature') {
        const f = featureLog.find((x) => x.feature.page_id === item.pageId && x.endedAt == null && !Number.isNaN(x.startedAt));
        if (f) Object.assign(f, { endedAt: sec(), endPos: here(), reason });
        return;
      }
      if (item.kind === 'filler') {
        const f = fillers.find((x) => x.area.page_id === item.pageId && x.endedAt == null);
        if (f) Object.assign(f, { endedAt: sec(), endPos: here(), reason });
        return;
      }
      const n = narrations.find((x) => x.pageId === item.pageId && x.endedAt == null);
      if (!n) return;
      const p = here();
      n.endedAt = sec();
      n.reason = reason;
      n.endPos = p;
      n.distAtEnd = p ? haversineM(p.lat, p.lon, n.landmark.lat, n.landmark.lon) : null;
    },
    onDrop: (item, reason) => {
      if (item.kind === 'feature') {
        const i = featureLog.findIndex((x) => x.feature.page_id === item.pageId && Number.isNaN(x.startedAt));
        if (i >= 0) featureLog.splice(i, 1);
        return;
      }
      if (item.kind === 'filler') {
        const i = fillers.findIndex((x) => x.area.page_id === item.pageId && Number.isNaN(x.startedAt));
        if (i >= 0) fillers.splice(i, 1);
        return;
      }
      engine.release(item.pageId);
      const lm = byId.get(item.pageId);
      if (lm) dropped.push({ landmark: lm, at: sec(), reason, dist: item.distance_m });
    },
  });

  const engine: ProximityEngine = new ProximityEngine({
    landmarksNear: (b) =>
      landmarks.filter((l) => l.lat >= b.minLat && l.lat <= b.maxLat && l.lon >= b.minLon && l.lon <= b.maxLon),
    isPlayed: (id) => played.has(id),
    onTrigger: (lm, dist, trig, fix) => {
      const landmark = byId.get(lm.page_id) ?? lm;
      const intro = narrationIntro(lm, fix, dist);
      pending.set(lm.page_id, {
        pageId: lm.page_id,
        landmark,
        intro: intro.text,
        triggeredAt: sec(),
        triggerPos: { t: sec(), lat: fix.coords.latitude, lon: fix.coords.longitude },
        startedAt: NaN,
        startPos: null,
        endedAt: null,
        endPos: null,
        reason: null,
        distAtTrigger: dist,
        distAtStart: dist,
        distAtEnd: null,
        triggerRadius: trig,
        speedMps: fix.speedMps,
      });
      queue.enqueue({
        pageId: lm.page_id,
        text: lm.script,
        // The opener is worded when speech starts, from wherever the car is by then.
        prelude: () => {
          const f = cur ?? fix;
          const text = narrationIntro(lm, f, haversineM(f.coords.latitude, f.coords.longitude, lm.lat, lm.lon)).text;
          const n = pending.get(lm.page_id);
          if (n) n.intro = text;
          return text;
        },
        distance_m: dist,
        estSeconds: lm.est_seconds + intro.seconds,
        baseRadiusM: lm.trigger_radius_m,
        maxDistanceM: Math.max(trig, lm.trigger_radius_m),
      });
      maxQueue = Math.max(maxQueue, queue.queue.length);
    },
    onCovered: (lm, by) => {
      covered.push({ landmark: byId.get(lm.page_id) ?? lm, by, at: sec() });
    },
    onExit: (id) => queue.remove(id),
    onDistance: (id, d, rel) => queue.updateDistance(id, d, rel),
  });

  const filler = new FillerScheduler(
    {
      areasHere: (f) => areasNear(opts.areas?.sightings ?? [], areaById, f.coords.latitude, f.coords.longitude),
      isPlayed: (id) => played.has(id),
      landmarkSoon: (f, withinSec) => engine.upcoming(f, 'sim', withinSec),
    },
    opts.filler,
  );

  const sightings = opts.features?.sightings;
  const along = new FeatureScheduler(
    {
      featuresHere: (f) => featuresNear(featureById, sightings, f.coords.latitude, f.coords.longitude),
      isPlayed: (id) => played.has(id),
      landmarkSoon: (f, withinSec) => engine.upcoming(f, 'sim', withinSec, true),
    },
    opts.along,
  );

  const closest = new Map<number, SimClosest>();
  for (const p of track) {
    for (const l of landmarks) {
      const d = haversineM(p.lat, p.lon, l.lat, l.lon);
      const c = closest.get(l.page_id);
      if (!c || d < c.d) closest.set(l.page_id, { d, t: p.t / 1000, lat: p.lat, lon: p.lon });
    }
  }

  const tf = new TrackFilter();
  let fixesIn = 0;
  let fixesAccepted = 0;
  let lastTs = t0;
  for (const fix of replayFixes(track, { noiseM: opts.noiseM ?? 0, dropoutRanges: opts.dropoutRanges ?? [], seed: opts.seed ?? 1, startEpochMs: t0 })) {
    fixesIn++;
    lastTs = fix.timestamp;
    await clock.advance(fix.timestamp);
    const clean = tf.push(fix);
    if (!clean) continue;
    fixesAccepted++;
    cur = clean;
    path.push({ t: sec(), lat: clean.coords.latitude, lon: clean.coords.longitude, heading: clean.heading });
    await engine.evaluate(clean, 'sim');
    if (featureById.size) {
      const { play, lost } = await along.pick(clean, queue);
      for (const id of lost) queue.remove(id);
      if (play) {
        featureLog.push({
          feature: play, runM: along.tracker.runM(play.page_id), queuedAt: sec(), startedAt: NaN, startPos: null,
          endedAt: null, endPos: null, reason: null, placeholder: !opts.features!.features.find((f) => f.page_id === play.page_id)?.script,
        });
        queue.enqueue(featureItem(play));
      }
    }
    if (areaById.size) {
      const area = await filler.pick(clean, queue, clock.now());
      if (area) {
        fillers.push({ area, quietSec: filler.quietMs(queue, clock.now()) / 1000, startedAt: NaN, startPos: null, endedAt: null, endPos: null, reason: null });
        queue.enqueue(fillerItem(area));
      }
    }
  }
  await clock.tick(opts.settleMs ?? 15 * 60_000);

  return {
    narrations,
    fillers,
    features: featureLog,
    dropped,
    covered,
    path,
    closest,
    rejects: tf.rejects,
    fixesIn,
    fixesAccepted,
    maxQueue,
    durationSec: (lastTs - t0) / 1000,
  };
}

/** A fixture feature with no script yet gets a placeholder of about a minute, so timing can be judged first. */
function withScript(f: Feature): Feature {
  if (f.script) return f;
  const words = 150;
  return { ...f, script: Array.from({ length: words }, () => 'placeholder').join(' '), est_seconds: Math.round(words / 2.6) };
}

/**
 * The shapes the phone would know at this point: the nearest corridor call's clipped geometry
 * when the fixture recorded sightings (as the phone replaces them on each sync), else the whole.
 */
function featuresNear(byId: ReadonlyMap<number, Feature>, sightings: FeatureFixture['sightings'], lat: number, lon: number, maxKm = 8): Feature[] {
  if (!sightings) return [...byId.values()];
  let best: NonNullable<FeatureFixture['sightings']>[number] | null = null;
  let bestD = maxKm * 1000;
  for (const s of sightings) {
    const d = haversineM(lat, lon, s.lat, s.lon);
    if (d <= bestD) {
      best = s;
      bestD = d;
    }
  }
  if (!best) return [];
  return best.features.flatMap((x) => {
    const f = byId.get(x.page_id);
    return f ? [{ ...f, geom: x.geom }] : [];
  });
}

/** The speech item for a rundown: its name as the opener, then the script, behind any landmark. */
export function fillerItem(a: Area, voice?: string): SpeechItem {
  const opener = `${spokenTitle(a.title)}.`;
  return { pageId: a.page_id, kind: 'filler', text: a.script, prelude: () => opener, distance_m: 0, estSeconds: a.est_seconds + 2, voice };
}

export interface Verdict {
  label: string;
  tone: 'good' | 'ok' | 'bad';
  detail: string;
}

const km = (m: number): string => (m / 1000).toFixed(2);

/** Did the narration finish before the car passed the landmark? */
export function narrationVerdict(n: SimNarration, closest: SimClosest | undefined): Verdict {
  if (!closest || n.endedAt == null) return { label: 'incomplete', tone: 'bad', detail: 'Narration did not finish.' };
  // A town is told as you cross its edge; when the road later passes its centre point is beside the point.
  if (isPlace(n.landmark)) {
    return { label: 'told on arrival', tone: 'good', detail: `Started at the edge of town, ${km(n.distAtTrigger)} km from its centre.` };
  }
  const overshoot = n.endedAt - closest.t;
  if (overshoot <= 0) {
    return {
      label: 'finished before passing',
      tone: 'good',
      detail: `Ended ${Math.round(-overshoot)} s before the closest point, ${km(n.distAtEnd ?? 0)} km away and still approaching.`,
    };
  }
  if (overshoot <= 25) {
    return {
      label: `ended ${Math.round(overshoot)} s past`,
      tone: 'ok',
      detail: `Still talking as you drew level, finishing ${km(n.distAtEnd ?? 0)} km beyond.`,
    };
  }
  return {
    label: `ended ${Math.round(overshoot)} s past`,
    tone: 'bad',
    detail: `Ran on well past the landmark, ending ${km(n.distAtEnd ?? 0)} km beyond it.`,
  };
}
